/**
 * Exclude package-protection (and related Mulberry) products from Upcart
 * cart-drawer upsells.
 *
 * Upcart calls `window.upcartModifyListOfUpsells(upsells)` with Shopify AJAX
 * product objects (`/products/{handle}.js`). Confirmed fields: `id`, `handle`,
 * `title`, `vendor`, `type`, `tags`. (Some Upcart mappers also expose
 * `productType`.) Returned items must keep a numeric `id` or Upcart discards
 * the whole result.
 *
 * A hardcoded ID list still lives in Upcart Custom HTML (`aboveHeader`). This
 * module wraps that assignment so rules-based filtering wins even if the
 * admin script overwrites the hook later. Keep the Redo IDs: the Redo
 * "Package Protection" product (`package-protection`, vendor `re:do`) is
 * still on the catalog.
 */

/**
 * Product IDs from the live Upcart Custom HTML list (Redo + Mulberry SKUs).
 *
 * Deliberate defense-in-depth, not redundancy: the rules below cannot match a
 * sparse upsell payload that exposes only `id`, so this list is the fallback
 * for that case. Everything a rule already catches is caught without it.
 *
 * MAINTENANCE GAP: this duplicates the hardcoded array in Upcart Custom HTML
 * (`aboveHeader`), which is not in git. The two can silently drift — a SKU
 * added to one is not added to the other. Prefer extending the rules below so
 * new Mulberry tranches need no change in either place.
 */
export const EXCLUDED_UPSELL_PRODUCT_IDS = [
  9768016871740, 9768013988156, 10348020367676, 10281529835836, 9769511387452,
  9769510764860, 9769510469948, 9768018641212, 9768017723708, 9768016052540,
  9768013398332, 9768013037884, 9762017116476, 10254704673084, 9769510994236,
  9768014807356,
];

const EXCLUDED_ID_SET = new Set(EXCLUDED_UPSELL_PRODUCT_IDS);

/**
 * Matched against the whole vendor, so this excludes *every* product from
 * these vendors, not only their protection plans. Intentional: Mulberry and
 * Redo supply nothing else we would upsell, and it is what makes new Mulberry
 * tranches drop out without a code change. Revisit if either vendor ever
 * supplies a product we do want to offer.
 */
const EXCLUDED_VENDORS = new Set(["mulberry", "re:do", "redo"]);

const EXCLUDED_TAGS = new Set(["mulberry-insurance"]);

const EXCLUDED_TYPE_FRAGMENTS = [
  "mulberry-insurance",
  "package_protection",
  "protection plan",
];

const EXCLUDED_HANDLE_FRAGMENTS = ["package-protection", "shipping-protection"];

const EXCLUDED_TITLE_FRAGMENTS = ["package protection", "shipping protection"];

/**
 * @param {unknown} tags
 * @returns {string[]}
 */
export function normalizeUpsellTags(tags) {
  if (Array.isArray(tags)) {
    return tags.map((tag) => String(tag).trim().toLowerCase()).filter(Boolean);
  }

  if (typeof tags === "string") {
    return tags
      .split(",")
      .map((tag) => tag.trim().toLowerCase())
      .filter(Boolean);
  }

  return [];
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

/**
 * @param {object} item
 * @returns {boolean}
 */
export function isExcludedUpsell(item) {
  if (!item || typeof item !== "object") return false;

  const id = Number(item.id);
  if (Number.isFinite(id) && EXCLUDED_ID_SET.has(id)) return true;

  const vendor = normalizeText(item.vendor);
  if (EXCLUDED_VENDORS.has(vendor)) return true;

  // Check every alias: Upcart mappers vary, and a benign value on one alias
  // must not mask a protection value on another.
  const productTypes = [item.productType, item.type, item.product_type]
    .map(normalizeText)
    .filter(Boolean);
  if (
    productTypes.some((productType) =>
      EXCLUDED_TYPE_FRAGMENTS.some((fragment) =>
        productType.includes(fragment),
      ),
    )
  ) {
    return true;
  }

  const tags = normalizeUpsellTags(item.tags);
  if (tags.some((tag) => EXCLUDED_TAGS.has(tag))) return true;

  const handle = normalizeText(item.handle);
  if (
    handle &&
    (EXCLUDED_HANDLE_FRAGMENTS.some((fragment) => handle.includes(fragment)) ||
      handle.startsWith("mulberry"))
  ) {
    return true;
  }

  const title = normalizeText(item.title);
  if (
    title &&
    EXCLUDED_TITLE_FRAGMENTS.some((fragment) => title.includes(fragment))
  ) {
    return true;
  }

  return false;
}

/**
 * @param {unknown} upsells
 * @returns {unknown}
 */
export function excludePackageProtectionUpsells(upsells) {
  if (!Array.isArray(upsells)) return upsells;

  return upsells.filter((item) => !isExcludedUpsell(item));
}

/**
 * Read the current `upcartModifyListOfUpsells` function from a property
 * descriptor. After the first install the property is an accessor (no
 * `value`), so reinstall must use the getter's return value or the wrapped
 * Custom HTML modifier is dropped.
 *
 * @param {object} target
 * @param {PropertyDescriptor | undefined} descriptor
 * @returns {Function | null}
 */
function readExistingModifier(target, descriptor) {
  if (!descriptor) return null;

  if (typeof descriptor.get === "function") {
    try {
      const current = descriptor.get.call(target);
      return typeof current === "function" ? current : null;
    } catch {
      return null;
    }
  }

  return typeof descriptor.value === "function" ? descriptor.value : null;
}

/**
 * Install (or reinstall) the Upcart hook on `target`, wrapping any existing
 * `upcartModifyListOfUpsells` so both the Custom HTML ID list and these rules
 * apply.
 *
 * @param {object} [target]
 */
export function installUpsellExclusions(target = window) {
  const descriptor = Object.getOwnPropertyDescriptor(
    target,
    "upcartModifyListOfUpsells",
  );

  let extraModifier = readExistingModifier(target, descriptor);

  // A Custom HTML script may wrap the hook with the conventional
  // `const previous = window.upcartModifyListOfUpsells; window.upcartModifyListOfUpsells = ...`
  // pattern. `previous` is then this same wrapper, so calling it would recurse
  // until the stack blew — and the `catch` below would swallow the RangeError,
  // silently dropping the admin filter. Re-entering just applies our rules.
  let applyingExtraModifier = false;

  const combined = (upsells) => {
    let list = upsells;

    if (!applyingExtraModifier && typeof extraModifier === "function") {
      applyingExtraModifier = true;
      try {
        const next = extraModifier(list);
        if (Array.isArray(next)) list = next;
      } catch {
        // Keep the original list if the wrapped Custom HTML script throws.
      } finally {
        applyingExtraModifier = false;
      }
    }

    return excludePackageProtectionUpsells(list);
  };

  // Another script may have already locked the property. Redefining it would
  // throw and abort this module, so fall back to a plain assignment when the
  // existing definition allows one and bail out otherwise.
  if (descriptor && !descriptor.configurable) {
    if (descriptor.writable || typeof descriptor.set === "function") {
      target.upcartModifyListOfUpsells = combined;
    }

    return;
  }

  Object.defineProperty(target, "upcartModifyListOfUpsells", {
    configurable: true,
    enumerable: true,
    get() {
      return combined;
    },
    set(fn) {
      extraModifier = typeof fn === "function" ? fn : null;
    },
  });
}

installUpsellExclusions();
